import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import confetti from "canvas-confetti";
import type { ProgressPhase, GenerationErrorCode } from "@/client/hooks/use-generation-stream";

type GenerationLoaderProps = {
  isOpen: boolean;
  onClose?: () => void;
  onRedirect?: () => void;
  formData: {
    jobRole: string;
    country: string;
    jobUrl?: string;
  };
  progress: number;
  currentPhase: ProgressPhase | null;
  currentMessage: string;
  isThinking?: boolean;
  error?: string | null;
  errorCode?: GenerationErrorCode | null;
  insights?: import("@/client/hooks/use-generation-stream").MarketInsights | null;
  phases?: Record<ProgressPhase, import("@/client/hooks/use-generation-stream").PhaseStatus>;
  pipelineStartTime?: number | null;
  inline?: boolean;
};

// ============ INLINE CSS ============
const cssStyles = `
@import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;500&family=Inter:wght@400;500;600;700;800;900&display=swap');

:root {
  --ext-navy-anchor: #1A3A4F; 
  --ext-ocean-teal: #2A7A8A; 
  --ext-bright-cyan: #5CD6D6; 
  --ext-electric-cyan: #5EE8E8; 
  --ext-terracotta: #B86A3A; 
  --ext-warm-orange: #E8885E;
  --ext-gradient: linear-gradient(135deg, #1a3a4f 0%, #2a7a8a 50%, #e8885e 100%);
}

.loader-modal-wrapper { font-family: 'Inter', sans-serif; }
.loader-modal { width: 1000px; height: 680px; background: #ffffff; border-radius: 20px; box-shadow: 0 40px 100px rgba(0, 0, 0, 0.35); border: 1px solid rgba(255,255,255,0.2); overflow: hidden; display: flex; flex-direction: column; color: #111827; position: relative; }

.window-header { height: 50px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; padding: 0 20px; gap: 15px; flex-shrink: 0; z-index: 20; position: relative; }
.dots { display: flex; gap: 8px; }
.dot { width: 12px; height: 12px; border-radius: 50%; box-shadow: inset 0 1px 2px rgba(0,0,0,0.1); }
.dot-r { background: #ef4444; } .dot-y { background: #eab308; } .dot-g { background: #22c55e; }
.header-title { font-size: 0.85rem; font-weight: 700; color: var(--ext-ocean-teal); flex: 1; text-align: center; font-family: 'Fira Code', monospace; display: flex; align-items: center; justify-content: center; gap: 8px; letter-spacing: 0.05em; }

.content-split { display: flex; flex: 1; overflow: hidden; z-index: 10; position: relative; transition: filter 0.5s; }

.left-panel { width: 360px; background: #f8fafc; border-inline-end: 1px solid #e2e8f0; display: flex; flex-direction: column; flex-shrink: 0; align-items: center; padding: 30px 20px; position: relative; overflow: hidden; transition: all 0.8s ease; }
.left-panel::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 200px; background: linear-gradient(180deg, rgba(92, 214, 214, 0.05) 0%, transparent 100%); pointer-events: none; }

.left-title { font-size: 1rem; font-weight: 800; color: var(--ext-navy-anchor); margin-bottom: 24px; text-transform: uppercase; letter-spacing: 0.05em; display: flex; align-items: center; gap: 8px; z-index: 2; transition: opacity 0.5s; }
.pulse-dot-loader { width: 8px; height: 8px; background: var(--ext-electric-cyan); border-radius: 50%; box-shadow: 0 0 12px var(--ext-bright-cyan); animation: pulseOp 1.5s infinite; }
@keyframes pulseOp { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

.resume-skeleton { width: 250px; height: 333px; background: #ffffff; border-radius: 10px; box-shadow: 0 15px 35px rgba(26, 58, 79, 0.08); border: 1px solid #e2e8f0; padding: 20px; position: relative; overflow: hidden; z-index: 2; transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275); }

.system-status-box { width: 250px; background: #ffffff; border-radius: 10px; border: 1px solid #e2e8f0; margin-top: 24px; padding: 15px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); z-index: 2; display: flex; flex-direction: column; gap: 10px; }
.status-hdr { display: flex; justify-content: space-between; align-items: center; font-size: 0.7rem; font-weight: 700; color: var(--ext-navy-anchor); text-transform: uppercase; letter-spacing: 0.05em; }
.status-badge { background: #dbeafe; color: var(--ext-ocean-teal); padding: 2px 6px; border-radius: 4px; font-size: 0.65rem; }

.wave-container { display: flex; align-items: flex-end; justify-content: space-between; height: 30px; gap: 2px; }
.wave-bar { flex: 1; background: var(--ext-bright-cyan); border-radius: 2px 2px 0 0; opacity: 0.8; animation: equalizer 1.5s ease-in-out infinite alternate; }
.wave-bar:nth-child(even) { background: var(--ext-ocean-teal); }
.wave-bar:nth-child(3n) { background: var(--ext-warm-orange); }
@keyframes equalizer { 0% { height: 10%; } 100% { height: 100%; } }

.sk-header { display: flex; gap: 12px; margin-bottom: 20px; border-bottom: 2px solid #f8fafc; padding-bottom: 15px; }
.sk-avatar { width: 40px; height: 40px; border-radius: 50%; background: #e2e8f0; transition: background 0.5s; animation: pulseSkel 2s infinite; }
.sk-title-blocks { flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 8px; }
.sk-text { height: 8px; background: #e2e8f0; border-radius: 4px; transition: background 0.5s; animation: pulseSkel 2s infinite; }
.sk-text.brand { background: var(--ext-bright-cyan); opacity: 0.5; }
.sk-text.w-full { width: 100%; }
.sk-text.w-3-4 { width: 75%; }
.sk-text.w-1-2 { width: 50%; }
.sk-text.w-1-4 { width: 25%; }

.sk-section { margin-bottom: 15px; }
.sk-section-title { height: 10px; width: 40%; background: #cbd5e1; border-radius: 4px; margin-bottom: 10px; transition: background 0.5s; animation: pulseSkel 2s infinite; }

.sk-block { display: flex; gap: 10px; margin-bottom: 8px; opacity: 0; transform: translateY(10px); transition: all 0.5s ease; }
.sk-block.visible { opacity: 1; transform: translateY(0); }
.sk-bullet { width: 6px; height: 6px; border-radius: 50%; background: var(--ext-warm-orange); margin-top: 2px; flex-shrink: 0; transition: background 0.5s; opacity: 0.8; }
.sk-lines { flex: 1; display: flex; flex-direction: column; gap: 6px; }

@keyframes pulseSkel { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

.laser-scanner { position: absolute; top: 0; left: 0; width: 100%; height: 2px; background: var(--ext-electric-cyan); box-shadow: 0 0 15px var(--ext-electric-cyan), 0 0 30px var(--ext-ocean-teal); z-index: 10; transition: opacity 0.5s; animation: scanDocument 2.5s ease-in-out infinite alternate; opacity: 0.8; pointer-events: none; }
@keyframes scanDocument { 0% { top: 0; } 100% { top: 100%; } }

.loader-modal.is-success .sk-avatar,
.loader-modal.is-success .sk-text,
.loader-modal.is-success .sk-section-title { animation: none; background: #cbd5e1; }
.loader-modal.is-success .sk-bullet { background: var(--ext-ocean-teal); }
.loader-modal.is-success .laser-scanner { opacity: 0; }
.loader-modal.is-success .resume-skeleton { transform: scale(1.1); box-shadow: 0 25px 50px -12px rgba(42, 122, 138, 0.4); border-color: var(--ext-ocean-teal); }
.loader-modal.is-success .left-title, .loader-modal.is-success .system-status-box { opacity: 0; }

.right-panel { flex: 1; display: flex; flex-direction: column; background: #ffffff; overflow: hidden; position: relative; }

.chat-scroll { flex: 1; padding: 30px 40px; overflow-y: auto; scrollbar-width: none; display: flex; flex-direction: column; gap: 28px; padding-bottom: 60px; }
.chat-scroll::-webkit-scrollbar { display: none; }

.chat-row { display: flex; gap: 16px; opacity: 0; transform: translateY(15px); animation: slideUpFade 0.5s forwards cubic-bezier(0.175, 0.885, 0.32, 1.275); }
@keyframes slideUpFade { to { opacity: 1; transform: translateY(0); } }

.agent-avatar { width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(135deg, #f1f5f9, #e2e8f0); color: var(--ext-navy-anchor); display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border: 1px solid #ffffff; }
.agent-avatar svg { width: 22px; height: 22px; }

.chat-content { flex: 1; display: flex; flex-direction: column; gap: 8px; }
.chat-meta { display: flex; align-items: center; gap: 10px; margin-bottom: 2px; }
.agent-name { font-weight: 700; font-size: 0.95rem; color: var(--ext-navy-anchor); }
.time-stamp { font-size: 0.75rem; color: #94a3b8; font-weight: 500;}

html[dir="rtl"] .chat-bubble { border-radius: 16px 0 16px 16px; }
.chat-bubble { border-radius: 0 16px 16px 16px; background: #f8fafc; border: 1px solid #e2e8f0; padding: 14px 18px; font-size: 0.95rem; color: #334155; line-height: 1.6; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }

html[dir="rtl"] .data-card::before { right: 0; left: auto; border-radius: 0 4px 4px 0; }
.data-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; margin-top: 6px; padding: 16px; box-shadow: 0 4px 12px -2px rgba(0,0,0,0.05); position: relative; overflow: hidden; }
.data-card::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--ext-ocean-teal); border-radius: 4px 0 0 4px; }
.data-card.warm::before { background: var(--ext-warm-orange); }

.card-hdr { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; border-bottom: 1px solid #f1f5f9; padding-bottom: 8px; }
.card-hdr h4 { margin: 0; font-size: 0.8rem; font-weight: 800; color: var(--ext-navy-anchor); display: flex; align-items: center; gap: 6px; text-transform: uppercase; letter-spacing: 0.05em; }
.card-hdr h4 svg { color: var(--ext-bright-cyan); width: 16px; height: 16px; }
.card-hdr.warm h4 svg { color: var(--ext-warm-orange); }
.stat-badge { font-size: 0.7rem; font-weight: 700; background: #f1f5f9; color: var(--ext-ocean-teal); padding: 3px 8px; border-radius: 6px; }

.node-graph { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
.node-row { display: flex; align-items: center; gap: 12px; }
.node-dot { width: 10px; height: 10px; background: var(--ext-bright-cyan); border-radius: 50%; box-shadow: 0 0 8px var(--ext-bright-cyan); flex-shrink: 0; }
.node-line { flex: 1; height: 2px; background: repeating-linear-gradient(90deg, #cbd5e1, #cbd5e1 4px, transparent 4px, transparent 8px); }
.node-label { font-family: 'Fira Code', monospace; font-size: 0.75rem; color: #475569; font-weight: 600; background: #f8fafc; padding: 4px 8px; border-radius: 4px; border: 1px solid #e2e8f0; }

html[dir="rtl"] .verb-arrow svg { transform: rotate(180deg); animation: slideLeft 1.5s infinite; }
.verb-swap { display: flex; align-items: center; justify-content: center; gap: 15px; margin-top: 10px; background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px dashed #cbd5e1; }
.verb-box { flex: 1; text-align: center; }
.verb-label { font-size: 0.7rem; color: #94a3b8; text-transform: uppercase; font-weight: 700; margin-bottom: 4px; }
.verb-word { font-weight: 800; font-size: 0.95rem; }
.verb-word.old { color: #ef4444; text-decoration: line-through; opacity: 0.7; }
.verb-word.new { color: var(--ext-ocean-teal); }
.verb-arrow svg { color: var(--ext-warm-orange); width: 20px; height: 20px; animation: slideRight 1.5s infinite; }

@keyframes slideRight { 0% { transform: translateX(-3px); } 50% { transform: translateX(3px); } 100% { transform: translateX(-3px); } }
@keyframes slideLeft { 0% { transform: translateX(3px) rotate(180deg); } 50% { transform: translateX(-3px) rotate(180deg); } 100% { transform: translateX(3px) rotate(180deg); } }

.bar-chart { display: flex; flex-direction: column; gap: 10px; }
.bar-row { display: flex; align-items: center; gap: 10px; font-size: 0.8rem; font-weight: 600; color: #475569; }
.bar-track { flex: 1; height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; box-shadow: inset 0 1px 2px rgba(0,0,0,0.05); }
.bar-fill { height: 100%; border-radius: 3px; position: relative; }
html[dir="rtl"] .bar-fill::after { animation: sweep-rtl 2s infinite; }
.bar-fill::after { content: ""; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent); animation: sweep 2s infinite; }
@keyframes sweep { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
@keyframes sweep-rtl { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }

.typing { display: flex; gap: 6px; padding: 8px 6px; align-items: center; }
.type-dot { width: 8px; height: 8px; background: var(--ext-ocean-teal); opacity: 0.6; border-radius: 50%; animation: bounceIndicator 1.4s infinite ease-in-out both; }
.type-dot:nth-child(1) { animation-delay: -0.32s; }
.type-dot:nth-child(2) { animation-delay: -0.16s; }
@keyframes bounceIndicator { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); opacity: 1; background: var(--ext-warm-orange); } }

.global-progress { height: 6px; background: #f1f5f9; flex-shrink: 0; width: 100%; position: absolute; bottom: 0; inset-inline-start: 0; z-index: 20;}
.global-fill { height: 100%; background: var(--ext-gradient); width: 0%; transition: width 0.5s ease; box-shadow: 0 0 10px var(--ext-ocean-teal); }

.success-overlay { position: absolute; inset: 0; background: rgba(248, 250, 252, 0.85); backdrop-filter: blur(8px); z-index: 40; display: flex; flex-direction: column; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity 0.6s ease; }
.loader-modal.is-success .success-overlay { opacity: 1; pointer-events: auto; }
.loader-modal.is-success .content-split { filter: blur(6px) grayscale(40%); }
.loader-modal.is-success .global-fill { background: var(--ext-ocean-teal); width: 100% !important; }

.success-a4-mock { position: absolute; top: 50%; left: 50%; width: 500px; height: 700px; background: #ffffff; border-radius: 8px; box-shadow: 0 25px 50px -12px rgba(42, 122, 138, 0.25); border: 1px solid #e2e8f0; display: flex; flex-direction: column; padding: 40px; opacity: 0; transform: translate(-50%, calc(-50% + 40px)) scale(0.95); transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1); z-index: 1; pointer-events: none; overflow: hidden; }
.loader-modal.is-success .success-a4-mock { opacity: 0.8; transform: translate(-50%, -50%) scale(1); transition-delay: 0.1s; }

.mock-header { display: flex; flex-direction: column; align-items: center; border-bottom: 2px solid #f1f5f9; padding-bottom: 20px; margin-bottom: 20px; gap: 12px; }
.mock-name { width: 40%; height: 14px; background: var(--ext-ocean-teal); opacity: 0.2; border-radius: 4px; }
.mock-contact { width: 60%; height: 8px; background: #cbd5e1; border-radius: 4px; }
.mock-section { display: flex; flex-direction: column; gap: 15px; margin-bottom: 20px; }
.mock-section-title { width: 25%; height: 10px; background: var(--ext-ocean-teal); opacity: 0.3; border-radius: 4px; }
.mock-item { display: flex; flex-direction: column; gap: 8px; }
.mock-line-full { width: 100%; height: 6px; background: #e2e8f0; border-radius: 3px; }
.mock-line-long { width: 80%; height: 6px; background: #e2e8f0; border-radius: 3px; }
.mock-line-med { width: 50%; height: 6px; background: #e2e8f0; border-radius: 3px; }

.success-box { display: flex; flex-direction: column; align-items: center; text-align: center; transform: translateY(30px) scale(0.9); transition: all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1); opacity: 0; z-index: 2; }
.loader-modal.is-success .success-box { transform: translateY(0) scale(1); opacity: 1; transition-delay: 0.3s; }

.success-confetti-canvas { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 50; }

.massive-counter-wrap { position: relative; width: 180px; height: 180px; display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }

.countdown-svg { position: absolute; top: 0; left: 0; width: 100%; height: 100%; transform: rotate(-90deg); }
.countdown-circle-bg { fill: none; stroke: #e2e8f0; stroke-width: 8; }
.countdown-circle { fill: none; stroke: var(--ext-ocean-teal); stroke-width: 8; stroke-linecap: round; stroke-dasharray: 502; stroke-dashoffset: 0; transition: stroke-dashoffset 1s linear; }

.massive-number { font-size: 6rem; font-weight: 900; color: var(--ext-navy-anchor); font-family: 'Inter', sans-serif; line-height: 1; z-index: 2; margin-top: 5px; }

.success-box h1 { font-size: 2.8rem; font-weight: 800; color: var(--ext-navy-anchor); margin: 0 0 10px 0; letter-spacing: -0.03em; }
.success-box p { font-size: 1.2rem; color: #475569; margin: 0 0 35px 0; font-weight: 500; }

.redirect-btn { background: var(--ext-gradient); color: white; padding: 18px 40px; border-radius: 12px; font-size: 1.2rem; font-weight: 800; border: none; cursor: pointer; display: inline-flex; align-items: center; gap: 12px; box-shadow: 0 15px 30px -5px rgba(42, 122, 138, 0.4); text-transform: uppercase; letter-spacing: 0.05em; transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
.redirect-btn:hover { transform: translateY(-3px) scale(1.02); box-shadow: 0 20px 35px -5px rgba(42, 122, 138, 0.5); }
.redirect-btn svg { width: 22px; height: 22px; transition: transform 0.2s; }
.redirect-btn:hover svg { transform: translateX(6px); }
html[dir="rtl"] .redirect-btn svg { transform: rotate(180deg); }
html[dir="rtl"] .redirect-btn:hover svg { transform: rotate(180deg) translateX(6px); }

@media (max-width: 850px) {
  .loader-modal { width: 100%; height: 100dvh; border-radius: 0; border: none; max-width: none; }
  .window-header { display: none; }
  .content-split { flex-direction: column; }
  
  .left-panel { width: 100%; height: auto; flex-direction: row; justify-content: space-evenly; align-items: center; padding: 15px; border-inline-end: none; border-bottom: 1px solid #e2e8f0; flex-shrink: 0; }
  .left-panel::before { display: none; }
  .left-title { display: none; }
  
  .resume-skeleton { width: 130px; height: 173px; padding: 10px; margin: 0; box-shadow: 0 4px 10px rgba(0,0,0,0.05); }
  .loader-modal.is-success .resume-skeleton { transform: scale(1.05); }
  
  .sk-header { margin-bottom: 10px; padding-bottom: 8px; }
  .sk-avatar { width: 24px; height: 24px; }
  .sk-section { margin-bottom: 8px; }
  .sk-section-title { margin-bottom: 6px; height: 6px; }
  .sk-block { gap: 6px; margin-bottom: 6px; }
  .sk-bullet { width: 4px; height: 4px; }
  .sk-lines { gap: 4px; }
  .sk-text { height: 4px; }
  
  .system-status-box { width: 160px; margin-top: 0; padding: 12px; }
  
  .chat-scroll { padding: 20px 15px; gap: 20px; padding-bottom: 60px; }
  .chat-bubble { font-size: 0.9rem; padding: 12px 14px; }
  
  .success-box { padding: 30px 20px; width: 90%; }
  .massive-counter-wrap { width: 140px; height: 140px; margin-bottom: 15px; }
  .massive-number { font-size: 4rem; }
  .success-box h1 { font-size: 2rem; }
  .success-box p { font-size: 1rem; }
  .redirect-btn { width: 100%; justify-content: center; font-size: 1.1rem; }
}

.loader-overlay {
    position: fixed;
    inset: 0;
    z-index: 99999;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0,0,0,0.85);
}
`;

export const GenerationLoader = ({
  isOpen,
  onClose,
  onRedirect,
  progress,
  error,
  errorCode,
  inline = false,
}: GenerationLoaderProps) => {
  const { i18n } = useLingui();
  const isRTL = i18n.locale.startsWith("ar");

  // Must be inside component so `t` is called after i18n.activate()
  // useMemo prevents recreation on every render (avoids infinite update loop)
  const chatMessages = useMemo(() => [
    { 
        agent: t`Data Ingest Node`, 
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>,
        text: t`Loading baseline profile schema. Structuring raw data points for the AI pipelines.`,
        renderHtml: () => (
            <div className="data-card">
                <div className="card-hdr">
                    <h4><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg> {t`Schema Extraction`}</h4>
                    <span className="stat-badge">{t`Valid`}</span>
                </div>
                <div className="node-graph">
                    <div className="node-row"><div className="node-dot"></div><div className="node-line"></div><span className="node-label">User Profile.JSON</span></div>
                    <div className="node-row"><div className="node-dot" style={{background:'var(--ext-warm-orange)', boxShadow: '0 0 8px var(--ext-warm-orange)'}}></div><div className="node-line"></div><span className="node-label">{t`Work History Array (14 blocks)`}</span></div>
                </div>
            </div>
        )
    },
    { 
        agent: t`Market Scout`, 
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>,
        text: t`Scraping 21k live job descriptions for your target role in your locale. Injecting core keyword logic.`,
        renderHtml: () => (
            <div className="data-card">
                <div className="card-hdr">
                    <h4><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg> {t`Live Market Vectors`}</h4>
                    <span className="stat-badge">94% {t`Overlap`}</span>
                </div>
                <div className="bar-chart">
                    <div className="bar-row">
                        <span style={{width: '100px'}}>{t`System Design`}</span>
                        <div className="bar-track"><div className="bar-fill" style={{width: '89%', background: 'var(--ext-ocean-teal)'}}></div></div>
                        <span>89%</span>
                    </div>
                    <div className="bar-row">
                        <span style={{width: '100px'}}>AWS / Cloud</span>
                        <div className="bar-track"><div className="bar-fill" style={{width: '72%', background: 'var(--ext-bright-cyan)'}}></div></div>
                        <span>72%</span>
                    </div>
                </div>
            </div>
        )
    },
    {
        agent: t`Keyword Extractor`,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"></path><path d="M2 17l10 5 10-5"></path></svg>,
        text: t`Identifying top ATS keywords from your target role. Matching industry terminology for maximum visibility.`,
        renderHtml: () => (
            <div className="data-card">
                <div className="card-hdr">
                    <h4><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2L2 7l10 5 10-5-10-5z"></path></svg> {t`Top ATS Keywords`}</h4>
                    <span className="stat-badge">12 {t`Matched`}</span>
                </div>
                <div className="bar-chart">
                    <div className="bar-row">
                        <span style={{ width: '100px' }}>{t`Leadership`}</span>
                        <div className="bar-track"><div className="bar-fill" style={{ width: '92%', background: 'var(--ext-ocean-teal)' }}></div></div>
                        <span>92%</span>
                    </div>
                    <div className="bar-row">
                        <span style={{ width: '100px' }}>{t`Problem Solving`}</span>
                        <div className="bar-track"><div className="bar-fill" style={{ width: '88%', background: 'var(--ext-bright-cyan)' }}></div></div>
                        <span>88%</span>
                    </div>
                </div>
            </div>
        ),
    },
    {
        agent: t`Culture Analyst`,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><path d="M12 6v6l4 2"></path></svg>,
        text: t`Analyzing tone and culture fit for your target market. Tailoring language to resonate with hiring managers.`,
        renderHtml: () => (
            <div className="data-card warm">
                <div className="card-hdr warm">
                    <h4><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"></circle></svg> {t`Tone Match`}</h4>
                    <span className="stat-badge" style={{ background: '#ffedd5', color: 'var(--ext-warm-orange)' }}>98%</span>
                </div>
                <div className="bar-chart">
                    <div className="bar-row">
                        <span style={{ width: '100px' }}>{t`Professional`}</span>
                        <div className="bar-track"><div className="bar-fill" style={{ width: '95%', background: 'var(--ext-warm-orange)' }}></div></div>
                        <span>95%</span>
                    </div>
                </div>
            </div>
        ),
    },
    {
        agent: t`Action Verb Optimizer`,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>,
        text: t`Replacing weak phrases with high-impact executive verbs to guarantee ATS success.`,
        renderHtml: () => (
            <div className="data-card warm">
                <div className="card-hdr warm">
                    <h4><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg> {t`Vocabulary Rewriting`}</h4>
                    <span className="stat-badge" style={{ background: '#ffedd5', color: 'var(--ext-warm-orange)' }}>+4 {t`Verbs`}</span>
                </div>
                <div className="verb-swap">
                    <div className="verb-box"><div className="verb-label">{t`Weak Phrase`}</div><div className="verb-word old">"{t`helped with`}"</div></div>
                    <div className="verb-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg></div>
                    <div className="verb-box"><div className="verb-label">{t`Strong Verb`}</div><div className="verb-word new">"{t`Orchestrated`}"</div></div>
                </div>
            </div>
        ),
    },
    {
        agent: t`STAR Method Engine`,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>,
        text: t`Quantifying achievements with measurable outcomes. Structuring experience using Situation-Task-Action-Result.`,
        renderHtml: () => (
            <div className="data-card">
                <div className="card-hdr">
                    <h4><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77"></polygon></svg> {t`Achievement Quantification`}</h4>
                    <span className="stat-badge">6 {t`Metrics`}</span>
                </div>
                <div className="node-graph">
                    <div className="node-row"><div className="node-dot"></div><div className="node-line"></div><span className="node-label">Situation → Task</span></div>
                    <div className="node-row"><div className="node-dot" style={{ background: 'var(--ext-warm-orange)', boxShadow: '0 0 8px var(--ext-warm-orange)' }}></div><div className="node-line"></div><span className="node-label">Action → Result</span></div>
                </div>
            </div>
        ),
    },
    {
        agent: t`The Architect`,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>,
        text: t`Virtual node connection successful. Mapping the fully parsed AST into the visual rendering engine...`,
    },
    {
        agent: t`ATS Compliance Scanner`,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><path d="M9 15l2 2 4-4"></path></svg>,
        text: t`Scanning resume against ATS parsing rules. Optimizing structure and keywords for applicant tracking systems.`,
        renderHtml: () => (
            <div className="data-card">
                <div className="card-hdr">
                    <h4><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path></svg> {t`Score Breakdown`}</h4>
                    <span className="stat-badge">87 {t`Score`}</span>
                </div>
                <div className="bar-chart">
                    <div className="bar-row">
                        <span style={{ width: '100px' }}>{t`Format`}</span>
                        <div className="bar-track"><div className="bar-fill" style={{ width: '94%', background: 'var(--ext-ocean-teal)' }}></div></div>
                        <span>94%</span>
                    </div>
                    <div className="bar-row">
                        <span style={{ width: '100px' }}>{t`Keywords`}</span>
                        <div className="bar-track"><div className="bar-fill" style={{ width: '82%', background: 'var(--ext-bright-cyan)' }}></div></div>
                        <span>82%</span>
                    </div>
                </div>
            </div>
        ),
    },
    {
        agent: t`Content Polisher`,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>,
        text: t`Final readability and impact pass. Ensuring consistency, clarity, and professional tone across all sections.`,
        renderHtml: () => (
            <div className="data-card warm">
                <div className="card-hdr warm">
                    <h4><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path></svg> {t`Readability Metrics`}</h4>
                    <span className="stat-badge" style={{ background: '#ffedd5', color: 'var(--ext-warm-orange)' }}>A+</span>
                </div>
                <div className="bar-chart">
                    <div className="bar-row">
                        <span style={{ width: '100px' }}>{t`Clarity`}</span>
                        <div className="bar-track"><div className="bar-fill" style={{ width: '96%', background: 'var(--ext-warm-orange)' }}></div></div>
                        <span>96%</span>
                    </div>
                </div>
            </div>
        ),
    },
    {
        agent: t`Final Assembly`,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg>,
        text: t`Build complete. Your resume is ready for the builder.`,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [i18n.locale]);

  const TOTAL_STEPS = 10;
  const WAIT_AT_STEP_INDEX = 8; // Content Polisher — second-to-last; wait here until progress >= 100
  
  const [currentMsgs, setCurrentMsgs] = useState<number[]>([]);
  const [isSuccess, setIsSuccess] = useState(false);
  const [countdown, setCountdown] = useState(8);
  const [blocksVisible, setBlocksVisible] = useState<number>(0);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const [isRendered, setIsRendered] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const confettiCanvasRef = useRef<HTMLCanvasElement>(null);
  const shownMsgs = useRef<Set<number>>(new Set());
  const timerRefs = useRef<ReturnType<typeof setTimeout>[]>([]);
  const peakProgressRef = useRef(0);

  // Track peak progress across renders (survives isOpen becoming false)
  peakProgressRef.current = Math.max(peakProgressRef.current, progress);

  // Latch open: once isOpen becomes true, stay rendered until success sequence finishes
  useEffect(() => {
    if (isOpen) setIsRendered(true);
  }, [isOpen]);

  // Confetti on success — fires from modal canvas so it stays inside the modal
  const fireConfetti = () => {
    const canvas = confettiCanvasRef.current;
    const myConfetti = canvas
      ? confetti.create(canvas, { resize: true, useWorker: true })
      : confetti;

    const duration = 8000;
    const end = Date.now() + duration;
    (function frame() {
      myConfetti({ particleCount: 6, angle: 60, spread: 65, origin: { x: 0, y: 1 }, colors: ['#5CD6D6', '#2A7A8A', '#E8885E'] });
      myConfetti({ particleCount: 6, angle: 120, spread: 65, origin: { x: 1, y: 1 }, colors: ['#5CD6D6', '#2A7A8A', '#E8885E'] });
      if (Date.now() < end) { requestAnimationFrame(frame); }
    }());
  };

  const fullReset = () => {
    timerRefs.current.forEach((t) => clearTimeout(t));
    timerRefs.current = [];
    setCurrentMsgs([]);
    setIsSuccess(false);
    setCountdown(8);
    setBlocksVisible(0);
    setSimulatedProgress(0);
    shownMsgs.current.clear();
    peakProgressRef.current = 0;
    setIsRendered(false);
  };

  // Reset when parent closes us — but NOT if backend already completed (let success play)
  useEffect(() => {
    if (!isOpen && isRendered && !isSuccess && peakProgressRef.current < 100) {
      fullReset();
    }
  }, [isOpen, isRendered, isSuccess]);

  // Timer-based message scheduling: first 2 steps quickly, then steps 2–8 every ~3–5s, pause at step 8 until progress >= 100
  useEffect(() => {
    if (!isRendered || isSuccess) return;

    const show = (idx: number) => {
      if (shownMsgs.current.has(idx)) return;
      shownMsgs.current.add(idx);
      setCurrentMsgs((prev) => [...prev, idx]);
      setSimulatedProgress((prev) => Math.min(90, Math.max(prev, (idx + 1) * (90 / TOTAL_STEPS))));
    };

    show(0);
    const t1 = setTimeout(() => show(1), 2000);
    timerRefs.current.push(t1);

    let delay = 3500;
    for (let i = 2; i <= WAIT_AT_STEP_INDEX; i++) {
      delay += 3500 + Math.random() * 1000;
      const stepIndex = i;
      const t = setTimeout(() => {
        if (shownMsgs.current.has(stepIndex)) return;
        show(stepIndex);
      }, delay);
      timerRefs.current.push(t);
    }

    return () => {
      timerRefs.current.forEach((t) => clearTimeout(t));
      timerRefs.current = [];
    };
  }, [isRendered, isSuccess]);

  // When backend completes (progress >= 100): show final step, then trigger success overlay
  useEffect(() => {
    if (!isRendered || progress < 100 || isSuccess) return;

    timerRefs.current.forEach((t) => clearTimeout(t));
    timerRefs.current = [];

    const finalStepIndex = TOTAL_STEPS - 1;
    if (!shownMsgs.current.has(finalStepIndex)) {
      shownMsgs.current.add(finalStepIndex);
      setCurrentMsgs((prev) => [...prev, finalStepIndex]);
    }
    setSimulatedProgress(100);

    const t = setTimeout(() => {
      setIsSuccess(true);
      fireConfetti();
    }, 1500);
    return () => clearTimeout(t);
  }, [isRendered, progress, isSuccess]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [currentMsgs]);

  // Prevent scroll when loader is visible
  useEffect(() => {
    if (isRendered && !inline) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isRendered, inline]);

  // Resume block visibility
  useEffect(() => {
    if (isRendered && !isSuccess) {
      const interval = setInterval(() => {
        setBlocksVisible(v => (v < 4 ? v + 1 : v));
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [isRendered, isSuccess]);

  const onRedirectRef = useRef(onRedirect);
  useEffect(() => {
    onRedirectRef.current = onRedirect;
  }, [onRedirect]);

  // Success Countdown timer — fires onRedirect at 0, then fully resets
  useEffect(() => {
    if (isSuccess && countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (isSuccess && countdown === 0) {
      if (onRedirectRef.current) onRedirectRef.current();
      const t = setTimeout(fullReset, 300);
      return () => clearTimeout(t);
    }
  }, [isSuccess, countdown]); // Removed onRedirect from deps to prevent infinite loops

  const getTimestamp = () => {
    const now = new Date();
    let hours = now.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const mins = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${mins} ${ampm}`;
  };

  // Calculate success SVG stroke
  const strokeDashoffset = useMemo(() => {
    const maxDash = 502;
    const percentage = countdown / 8;
    return maxDash - (maxDash * percentage);
  }, [countdown]);

  if (!isRendered) return null;

  const content = (
    <div className="loader-modal-wrapper">
      <style>{cssStyles}</style>
      <div className={`loader-modal ${isSuccess ? 'is-success' : ''}`}>
        <div className="window-header">
            <div className="dots">
                <div className="dot dot-r"></div><div className="dot dot-y"></div><div className="dot dot-g"></div>
            </div>
            <div className="header-title">INTELLI-STYLINGCV CORE</div>
            <div style={{width: 44}}></div>
        </div>

        {error ? (
          <div className="p-8 flex items-center justify-center flex-1">
            <div className="border-red-200 bg-red-50 rounded-xl p-6 text-center w-full max-w-md">
              <p className="text-lg font-bold text-red-600 mb-2">{t`Something went wrong`}</p>
              <p className="text-sm text-red-500">{error}</p>
              {onClose && (
                <button
                  onClick={onClose}
                  className="mt-6 rounded-lg bg-red-100 px-6 py-2 text-sm font-medium text-red-600 hover:bg-red-200 transition-colors"
                >
                  {t`Close And Try Again`}
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="content-split">
                <div className="left-panel">
                    <div className="left-title"><div className="pulse-dot-loader"></div> {t`Live Build Engine`}</div>
                    <div className="resume-skeleton">
                        <div className="laser-scanner"></div>
                        
                        <div className="sk-header">
                            <div className="sk-avatar"></div>
                            <div className="sk-title-blocks">
                                <div className="sk-text w-full brand"></div>
                                <div className="sk-text w-1-2"></div>
                            </div>
                        </div>

                        <div className="sk-section">
                            <div className="sk-section-title"></div>
                            <div className={`sk-block ${blocksVisible >= 1 ? 'visible' : ''}`}><div className="sk-bullet"></div><div className="sk-lines"><div className="sk-text w-full"></div><div className="sk-text w-3-4"></div></div></div>
                            <div className={`sk-block ${blocksVisible >= 2 ? 'visible' : ''}`}><div className="sk-bullet"></div><div className="sk-lines"><div className="sk-text w-full"></div><div className="sk-text w-1-2"></div></div></div>
                        </div>

                        <div className="sk-section">
                            <div className="sk-section-title"></div>
                            <div className={`sk-block ${blocksVisible >= 3 ? 'visible' : ''}`}><div className="sk-bullet"></div><div className="sk-lines"><div className="sk-text w-full"></div></div></div>
                            <div className={`sk-block ${blocksVisible >= 4 ? 'visible' : ''}`}><div className="sk-bullet"></div><div className="sk-lines"><div className="sk-text w-full"></div><div className="sk-text w-3-4"></div></div></div>
                        </div>
                    </div>

                    <div className="system-status-box">
                        <div className="status-hdr">
                            <span>{t`Agent Orchestration Array`}</span>
                            <span className="status-badge" style={isSuccess ? {background: '#dcfce7', color: '#16a34a'} : undefined}>
                                {isSuccess ? t`Completed` : t`Active`}
                            </span>
                        </div>
                        <div className="wave-container" style={isSuccess ? {opacity: 0.2} : undefined}>
                            {[1.1, 0.9, 1.3, 0.8, 1.5, 1.0, 1.2, 0.7, 1.4].map((dur, i) => (
                              <div key={i} className="wave-bar" style={{ animationDuration: `${dur}s`, animationPlayState: isSuccess ? 'paused' : 'running' }}></div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="right-panel">
                    <div className="chat-scroll" ref={chatRef}>
                       {currentMsgs.map((msgIdx) => {
                          const msg = chatMessages[msgIdx];
                          return (
                            <div key={msgIdx} className="chat-row">
                                <div className="agent-avatar">{msg.icon}</div>
                                <div className="chat-content">
                                    <div className="chat-meta">
                                        <span className="agent-name">{msg.agent}</span>
                                        <span className="time-stamp">{getTimestamp()}</span>
                                    </div>
                                    <div className="chat-bubble font-serif">{msg.text}</div>
                                    {msg.renderHtml && msg.renderHtml()}
                                </div>
                            </div>
                          );
                       })}

                       {!isSuccess && (
                        <div className="chat-row ml-0 mt-auto">
                            <div className="agent-avatar" style={{background: '#f1f5f9', borderColor: '#e2e8f0'}}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                            </div>
                            <div className="chat-content">
                                <div className="chat-meta"><span className="agent-name">{t`System Processing...`}</span></div>
                                <div className="chat-bubble" style={{background: 'transparent', border: 'none', padding: '6px 0', boxShadow: 'none'}}>
                                    <div className="typing"><div className="type-dot"></div><div className="type-dot"></div><div className="type-dot"></div></div>
                                </div>
                            </div>
                        </div>
                       )}
                    </div>

                    <div className="global-progress">
                        <div className="global-fill" style={{ width: `${Math.max(simulatedProgress, progress)}%` }}></div>
                    </div>
                </div>
            </div>

            <div className="success-overlay">
                <canvas ref={confettiCanvasRef} className="success-confetti-canvas" />
                
                {/* Background A4 Mock Resume */}
                <div className="success-a4-mock">
                    <div className="mock-header">
                        <div className="mock-name"></div>
                        <div className="mock-contact"></div>
                    </div>
                    <div className="mock-section">
                        <div className="mock-section-title"></div>
                        <div className="mock-item">
                            <div className="mock-line-full"></div>
                            <div className="mock-line-long"></div>
                            <div className="mock-line-med"></div>
                        </div>
                        <div className="mock-item mt-2">
                            <div className="mock-line-full"></div>
                            <div className="mock-line-med"></div>
                        </div>
                    </div>
                    <div className="mock-section">
                        <div className="mock-section-title"></div>
                        <div className="mock-item">
                            <div className="mock-line-full"></div>
                            <div className="mock-line-long"></div>
                        </div>
                    </div>
                </div>

                <div className="success-box">
                    <div className="massive-counter-wrap">
                        <svg className="countdown-svg" viewBox="0 0 180 180">
                            <circle className="countdown-circle-bg" cx="90" cy="90" r="80"></circle>
                            <circle className="countdown-circle" cx="90" cy="90" r="80" style={{strokeDashoffset}}></circle>
                        </svg>
                        <span className="massive-number">{countdown}</span>
                    </div>
                    
                    <h1>{t`Your Resume is Ready!`}</h1>
                    <p>{t`The Stylion Engine has crafted your perfect profile.`}</p>
                    
                    <button className="redirect-btn" onClick={onRedirect}>
                        <span>{t`Opening Builder Now`}</span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                    </button>
                </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (inline) {
    return <div className="w-full py-4 px-2">{content}</div>;
  }

  return createPortal(
    <div className="loader-overlay" dir={isRTL ? "rtl" : "ltr"}>
      {content}
    </div>,
    document.body
  );
};

export default GenerationLoader;
